import * as React from 'react';
import { alpha, keyframes } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { visuallyHidden } from '@mui/utils';
import { TransactionData } from './PaymentTypes';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, Snackbar, SnackbarCloseReason } from '@mui/material';
import { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import axios from 'axios';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DynamicModal from './DynamicModal';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';


const PERCENTAGE_CHANGE_THRESHOLD = 20;

const blink = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

// amount per reward source (SEGPOOL, SEGPROXY), one line each; "-" for transactions from before sources were tracked
function SourcesList({ sources }: { sources: Record<string, number> | null }) {
    if (!sources || Object.keys(sources).length === 0) return <>-</>;

    return (
        <Box component="span" sx={{ display: 'inline-grid', gridTemplateColumns: 'auto auto', columnGap: 1, textAlign: 'right' }}>
            {Object.entries(sources).map(([source, amount]) => (
                <React.Fragment key={source}>
                    <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.8em', alignSelf: 'center' }}>{source}</Box>
                    <Box component="span">{amount.toFixed(8)}</Box>
                </React.Fragment>
            ))}
        </Box>
    );
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (
    a: { [key in Key]: any },
    b: { [key in Key]: any },
) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function formatDateTime(dateString: string) {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
}

interface HeadCell {
    disablePadding: boolean;
    id: keyof TransactionData;
    label: string;
    numeric: boolean;
    sortable?: boolean;
}

const headCells: readonly HeadCell[] = [
    {
        id: 'username',
        numeric: true,
        disablePadding: false,
        label: 'user',
    },
    {
        id: 'customerGroupName',
        numeric: true,
        disablePadding: false,
        label: 'Customer',
    },
    {
        id: 'amount',
        numeric: true,
        disablePadding: false,
        label: 'Amount',
    },
    {
        id: 'sources',
        numeric: true,
        disablePadding: false,
        label: 'Sources',
        sortable: false,
    },
    {
        id: 'paymentMode',
        numeric: true,
        disablePadding: false,
        label: 'Mode',
    },
    {
        id: 'hashRateInTh',
        numeric: true,
        disablePadding: false,
        label: 'hash (TH)',
    },
    {
        id: 'created',
        numeric: true,
        disablePadding: false,
        label: 'created',
    },
    {
        id: '' as keyof TransactionData,
        numeric: false,
        disablePadding: false,
        label: '',
    },
];

interface EnhancedTableProps {
    numSelected: number;
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof TransactionData) => void;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {

    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
        props;
    const createSortHandler =
        (property: keyof TransactionData) => (event: React.MouseEvent<unknown>) => {
            onRequestSort(event, property);
        };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox" sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5', color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000', padding: '6px' }}>
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            'aria-label': 'select all desserts',
                        }}
                    />
                </TableCell>
                <TableCell padding="checkbox" sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5', color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000', padding: '6px' }} />
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5', color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000', padding: '6px' }}
                    >
                        {headCell.sortable === false ? headCell.label : (
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}
                            >
                                {headCell.label}
                                {orderBy === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        )}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}
interface EnhancedTableToolbarProps {
    selectedItems: string[];
    refresh: () => void;
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
    const { selectedItems, refresh } = props;
    const [approved, setApproved] = useState<boolean>(false);
    const [rejected, setRejected] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [alertMsg, setAlertMsg] = useState<String | null>(null)
    const [alertType, setAlertType] = useState<"error" | "success">("success")

    function handleDialogClose() {
        if (loading) return;
        setApproved(false)
        setRejected(false)
    }
    function handleSnackbarClose() {
        setAlertMsg(null)
    }
    function doApprove() {
        processData("APPROVED")
    }
    function doReject() {
        processData("REJECTED")
    }
    function processData(status: string) {
        if (selectedItems.length == 0) {
            setAlertMsg("No items selected")
            setAlertType("error");
            return;
        }
        setLoading(true);
        axios.post('/api/segPoolRewardDistribution/update', { ids: selectedItems, status: status }, getAxiosConfig())
            .then(x => {
                setAlertMsg("Updated")
                setAlertType("success");
                setLoading(false);
                handleDialogClose();
                refresh()
            })
            .catch(e => {
                setLoading(false);
                setAlertMsg("Error updating info");
                setAlertType("error");
            })
    }
    return (
        selectedItems.length > 0 ?
            <Toolbar
                sx={[
                    {
                        pl: { sm: 2 },
                        pr: { xs: 1, sm: 1 },
                    },
                    selectedItems.length > 0 && {
                        bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                    },
                ]}
            >
                {selectedItems.length > 0 ? (
                    <Typography
                        sx={{ flex: '1 1 100%' }}
                        color="inherit"
                        variant="subtitle1"
                        component="div"
                    >
                        {selectedItems.length} selected
                    </Typography>
                ) : (
                    <Typography
                        sx={{ flex: '1 1 100%' }}
                        variant="h6"
                        id="tableTitle"
                        component="div"
                    >

                    </Typography>
                )}
                {selectedItems.length > 0 ? (
                    <>
                        <Tooltip title="Reject">
                            <IconButton aria-label="Reject" color="error" sx={{ m: 1 }} onClick={() => { setRejected(true) }}>
                                <BlockIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Approve">
                            <IconButton aria-label="Approve" color="success" sx={{ m: 1 }} onClick={() => { setApproved(true) }}>
                                <CheckIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                ) : (
                    <></>
                )}
                <Dialog
                    open={approved}
                    onClose={handleDialogClose}
                    aria-labelledby="Approve transactions" >
                    <DialogContent>
                        <DialogContentText >
                            Approve all selected transactions
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose}> Cancel </Button>
                        <Button onClick={doApprove} autoFocus disabled={loading}> Approve </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={rejected}
                    onClose={handleDialogClose}
                    aria-labelledby="Reject transactions" >
                    <DialogContent>
                        <DialogContentText >
                            Reject all selected transactions
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose}> Cancel </Button>
                        <Button onClick={doReject} autoFocus disabled={loading}> Reject </Button>
                    </DialogActions>
                </Dialog>
                <Snackbar open={alertMsg != null} autoHideDuration={6000} onClose={handleSnackbarClose}>
                    <Alert
                        onClose={handleSnackbarClose}
                        severity={alertType}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {alertMsg}
                    </Alert>
                </Snackbar>
            </Toolbar>
            : <></>
    );
}

interface TableComponentProps {
    rows: TransactionData[];
    refresh: () => void;
    status: string;
    recentTransactions: Record<string, TransactionData[]> | null;
}

const PaymentTable: React.FC<TableComponentProps> = ({ rows, refresh, status, recentTransactions }) => {
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof TransactionData>('id');
    const [selected, setSelected] = React.useState<readonly string[]>([]);
    const [page, setPage] = React.useState(0);
    const [dense, setDense] = React.useState(false);
    const [rowsPerPage, setRowsPerPage] = React.useState(25);
    const [selectedAmounts, setSelectedAmounts] = useState<readonly string[]>([]);
    const [snackOpen, setSnackOpen] = React.useState(false);
    const [snackText, setSnackText] = React.useState('');

    const [open, setOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState<string>("");
    const [modalContent, setModalContent] = useState<string | null>(null);

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof TransactionData,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = rows.map((n) => n.id);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, id: string, amount: string) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: readonly string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);

        const selectedAmountIndex = selectedAmounts.indexOf(amount);
        let newSelectedAmounts: readonly string[] = [];

        if (selectedAmountIndex === -1) {
            newSelectedAmounts = newSelectedAmounts.concat(selectedAmounts, amount);
        } else if (selectedAmountIndex === 0) {
            newSelectedAmounts = newSelectedAmounts.concat(selectedAmounts.slice(1));
        } else if (selectedAmountIndex === selectedAmounts.length - 1) {
            newSelectedAmounts = newSelectedAmounts.concat(selectedAmounts.slice(0, -1));
        } else if (selectedAmountIndex > 0) {
            newSelectedAmounts = newSelectedAmounts.concat(
                selectedAmounts.slice(0, selectedAmountIndex),
                selectedAmounts.slice(selectedAmountIndex + 1),
            );
        }

        setSelectedAmounts(newSelectedAmounts);
        const totalAmount = newSelectedAmounts.reduce((acc, current) => {
            return acc + parseFloat(current);
        }, 0);
    };


    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDense(event.target.checked);
    };

    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    const visibleRows = React.useMemo(
        () =>
            [...rows]
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [order, orderBy, page, rowsPerPage],
    );

    const handleClose = (
        event?: React.SyntheticEvent | Event,
        reason?: SnackbarCloseReason,
    ) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackOpen(false);
    };

    const handleOpenModal = (title: string, content: string | null) => {
        setModalTitle(title);
        setModalContent(content);
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
        setModalContent(null);
    };

    const handleDownloadCSV = () => {
        if (!rows || rows.length === 0) {
            setSnackText("No data to download");
            setSnackOpen(true);
            return;
        }
        const headers: (keyof TransactionData)[] = [
            // "dummy",
            "username",
            "customerGroupName",
            "amount",
            "paymentMode",
            "hashRateInTh",
            "created",
            "rawError"
        ];

        const csvRows = [
            headers.join(","),
            ...rows.map((row) =>
                headers
                    .map((field) => {
                        let cell: any = row[field];
                        // if (field === "dummy") {
                        //     cell = row.dummy === undefined ? "N/A" : row.dummy ? "red" : "green";
                        // }
                        if (field === "rawError") {
                            cell = cell ? String(cell) : "";
                        }

                        cell = cell ?? "";
                        cell = String(cell).replace(/"/g, '""');
                        return `"${cell}"`;
                    })
                    .join(",")
            ),
        ];
        const csvString = csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "payment_data.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    function Row(props: { row: TransactionData, isItemSelected: boolean, labelId: string, hasRecentTransactions: boolean }) {
        const { row, isItemSelected, labelId, hasRecentTransactions } = props;
        const [open, setOpen] = React.useState(false);
        const history = recentTransactions ? recentTransactions[row.username + " " + row.paymentMode] : [];
        const recentTransaction = history && history.length > 0 ? history.reduce((prev, current) => (new Date(prev.createdTime) > new Date(current.createdTime)) ? prev : current) : null;
        const recentAmount = recentTransaction ? recentTransaction.amount : 0;
        const currentAmountVal = parseFloat(row.amount);
        const recentAmountVal = parseFloat(String(recentAmount));
        const percentageChange = recentAmountVal > 0 ? ((currentAmountVal - recentAmountVal) / recentAmountVal) * 100 : 0;


        return (
            <React.Fragment>
                <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                    sx={{
                        // a large change from the last payout blinks; a dummy account's row is tinted light red
                        backgroundColor: (theme) => Math.abs(percentageChange) > PERCENTAGE_CHANGE_THRESHOLD
                            ? alpha(theme.palette.error.main, 0.3)
                            : row.dummy ? alpha(theme.palette.error.main, 0.1) : 'inherit',
                        animation: Math.abs(percentageChange) > PERCENTAGE_CHANGE_THRESHOLD ? `${blink} 2s infinite ease-in-out` : 'none'
                    }}
                >
                    <TableCell padding="checkbox">
                        <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            inputProps={{
                                'aria-labelledby': labelId,
                            }}
                            sx={{ cursor: 'pointer' }}
                            onClick={(event) => handleClick(event, row.id, row.amount)}
                        />
                    </TableCell>
                    <TableCell>
                        {(hasRecentTransactions) && (
                            <IconButton
                                aria-label="expand row"
                                size="small"
                                onClick={() => setOpen(!open)}
                            >
                                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                        )}
                    </TableCell>
                    <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                        align="right"
                    >
                        {row.username}
                    </TableCell>
                    <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                        align="right"
                    >
                        {row.customerGroupName}
                    </TableCell>
                    <TableCell align="right">
                        {parseFloat(row.amount).toFixed(8)}
                        {recentAmountVal > 0 && (
                            <span style={{ marginLeft: '8px', color: percentageChange >= 0 ? 'green' : 'red', fontSize: '0.8em' }}>
                                {percentageChange > 0 ? '+' : ''}{percentageChange.toFixed(2)}%
                            </span>
                        )}
                    </TableCell>
                    <TableCell align="right"><SourcesList sources={row.sources} /></TableCell>
                    <TableCell align="right">{row.paymentMode}</TableCell>
                    <TableCell align="right">{row.hashRateInTh.toFixed(2)}</TableCell>
                    <TableCell align="right">{row.created}</TableCell>
                    <TableCell>
                        {row?.rawError && (
                            <WarningAmberIcon
                                onClick={() => handleOpenModal("Error Details", row?.rawError)}
                                sx={{ color: '#FF0000', mr: 1, cursor: 'pointer' }}
                            />
                        )}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1 }}>
                                <Table size="small" aria-label="purchases">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell > &nbsp; </TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                            <TableCell align="right">Payment Mode</TableCell>
                                            <TableCell align="right">Mode</TableCell>
                                            <TableCell align="right">Hash (TH)</TableCell>
                                            <TableCell align="right">Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {history?.map((historyRow) => {
                                            return (
                                                <TableRow key={historyRow.id}>
                                                    <TableCell>&nbsp;</TableCell>
                                                    <TableCell align="right">{historyRow.transactionStatus}</TableCell>
                                                    <TableCell align="right">{parseFloat(historyRow.amount).toFixed(8)}</TableCell>
                                                    <TableCell align="right">{historyRow.paymentMode}</TableCell>
                                                    <TableCell align="right">{historyRow.hashRateInTh}</TableCell>
                                                    <TableCell align="right">
                                                        {formatDateTime(historyRow.createdTime)}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );
    }



    return (
        <>
            {status === 'pending' &&
                <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: (theme) => alpha(theme.palette.error.main, 0.1), border: 1, borderColor: 'divider' }} />
                        <Typography variant="body2" color="text.secondary">Dummy account</Typography>
                    </Box>
                    <Button variant="contained" color="primary" onClick={handleDownloadCSV}>
                        Download CSV
                    </Button>
                </Box>
            }

            <Paper sx={{ width: '100%', padding: '16px' }}>
                <Box>
                    <EnhancedTableToolbar selectedItems={[...selected]} refresh={refresh} />
                    <TableContainer>
                        <Table
                            sx={{ minWidth: 750 }}
                            aria-labelledby="tableTitle"
                            size={dense ? 'small' : 'medium'}
                        >
                            <EnhancedTableHead
                                numSelected={selected.length}
                                order={order}
                                orderBy={orderBy}
                                onSelectAllClick={handleSelectAllClick}
                                onRequestSort={handleRequestSort}
                                rowCount={rows.length}
                            />
                            <TableBody>
                                {visibleRows.map((row, index) => {
                                    const isItemSelected = selected.includes(row.id);
                                    const labelId = `enhanced-table-checkbox-${index}`;

                                    const history = recentTransactions ? recentTransactions[row.username + " " + row.paymentMode] : [];
                                    const hasRecentTransactions = history && history.length > 0;

                                    return (
                                        <Row key={row.id} row={row} isItemSelected={isItemSelected} labelId={labelId} hasRecentTransactions={hasRecentTransactions} />
                                    );
                                })}
                                {emptyRows > 0 && (
                                    <TableRow
                                        style={{
                                            height: (dense ? 33 : 53) * emptyRows,
                                        }}
                                    >
                                        <TableCell colSpan={10} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        paddingX: '16px'
                    }}>
                        <FormControlLabel
                            control={<Switch checked={dense} onChange={handleChangeDense} />}
                            label="Dense padding"
                        />
                        <TablePagination
                            rowsPerPageOptions={[25, 50, 75, 100]}
                            component="div"
                            count={rows.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Box>
                </Box>

                <Snackbar
                    open={snackOpen}
                    autoHideDuration={6000}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleClose}
                        variant="filled"
                        sx={{ width: '100%' }}
                        severity="error"
                    >
                        {snackText}
                    </Alert>
                </Snackbar>
            </Paper>
            <DynamicModal
                open={open}
                modalTitle={modalTitle}
                modalContent={modalContent}
                handleCloseModal={handleCloseModal}
            />
        </>
    );
};

export default PaymentTable;
